import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { WarningCircle, CheckCircle, Sparkle, DownloadSimple, PaperPlaneTilt, X, Check, Eye, ArrowLeft, Lock, Plug, CaretDown, Trash, Plus, Calculator, Signature, Copy, List as ListIcon, EyeSlash, ArrowsDownUp } from "@phosphor-icons/react";

const fmtINR = (paise) => `₹${((paise||0)/100).toLocaleString("en-IN")}`;

// Section arrangement: drag-to-reorder + rename + hide toggle per proposal.
// Compact list; each row: drag handle · label input · hide toggle.
const SectionArranger = ({ proposalId, disabled }) => {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [dirty, setDirty] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [dragIdx, setDragIdx] = React.useState(null);
  const [expanded, setExpanded] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/proposals/${proposalId}/sections`);
      setItems(data.sections || []);
      setDirty(false);
    } catch (e) { toast.error("Failed to load section arrangement"); }
    finally { setLoading(false); }
  }, [proposalId]);
  React.useEffect(() => { load(); }, [load]);

  const update = (i, patch) => {
    setItems(prev => prev.map((it, idx) => idx === i ? { ...it, ...patch } : it));
    setDirty(true);
  };
  const move = (from, to) => {
    if (from === to || to < 0 || to >= items.length) return;
    setItems(prev => {
      const copy = prev.slice();
      const [moved] = copy.splice(from, 1);
      copy.splice(to, 0, moved);
      return copy;
    });
    setDirty(true);
  };
  const resetOrder = () => { load(); toast.info("Discarded changes"); };
  const restoreDefaults = async () => {
    if (!window.confirm("Restore the default section order and labels?")) return;
    setSaving(true);
    try {
      await api.put(`/proposals/${proposalId}/sections`, { sections: [] });
      toast.success("Restored default order");
      load();
    } catch (e) { toast.error("Failed to restore"); } finally { setSaving(false); }
  };
  const save = async () => {
    setSaving(true);
    try {
      const payload = items.map(it => ({ key: it.key, label: it.label, hidden: !!it.hidden }));
      await api.put(`/proposals/${proposalId}/sections`, { sections: payload });
      toast.success("Section arrangement saved");
      setDirty(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-slate-200 p-5" data-testid="section-arranger">
      <button type="button" onClick={() => setExpanded(v => !v)} className="w-full flex items-center justify-between" data-testid="section-arranger-toggle">
        <div className="flex items-center gap-2">
          <ListIcon size={18} className="text-slate-700"/>
          <h3 className="font-display font-bold">Section arrangement</h3>
          <span className="label-micro">{items.filter(i => !i.hidden).length} visible · {items.filter(i => i.hidden).length} hidden</span>
        </div>
        <div className="flex items-center gap-3">
          {dirty && <span data-testid="section-arranger-dirty" className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-sm"><span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"/>Unsaved</span>}
          <CaretDown size={14} className={`text-slate-500 transition-transform ${expanded ? "rotate-180" : ""}`}/>
        </div>
      </button>
      {expanded && (
        <div className="mt-4">
          <p className="text-xs text-slate-500 mb-3">Drag rows to reorder. Rename any heading. Hide sections you don't need. Changes apply to Preview + DOCX/PDF export.</p>
          {loading && <div className="text-xs text-slate-500">Loading…</div>}
          {!loading && (
            <ul className="space-y-1.5" data-testid="section-arranger-list">
              {items.map((it, i) => (
                <li
                  key={it.key}
                  data-testid={`section-row-${it.key}`}
                  draggable={!disabled}
                  onDragStart={() => setDragIdx(i)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => { if (dragIdx != null) move(dragIdx, i); setDragIdx(null); }}
                  className={`flex items-center gap-2 border border-slate-200 bg-slate-50 hover:bg-white rounded-sm px-2 py-1.5 ${dragIdx === i ? "opacity-40" : ""} ${it.hidden ? "opacity-60" : ""}`}
                >
                  <span className="cursor-grab text-slate-400" title="Drag to reorder"><ArrowsDownUp size={14}/></span>
                  <span className="text-xs text-slate-500 tabular-nums w-5 text-right">{i + 1}.</span>
                  <input
                    value={it.label}
                    disabled={disabled}
                    data-testid={`section-label-${it.key}`}
                    onChange={(e) => update(i, { label: e.target.value })}
                    className="flex-1 border border-slate-200 rounded-sm px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white disabled:cursor-not-allowed"
                  />
                  {it.label !== it.default_label && (
                    <button type="button" title="Reset label" onClick={() => update(i, { label: it.default_label })} className="text-[10px] text-blue-600 hover:underline">reset</button>
                  )}
                  <button
                    type="button"
                    disabled={disabled}
                    data-testid={`section-toggle-${it.key}`}
                    onClick={() => update(i, { hidden: !it.hidden })}
                    title={it.hidden ? "Show this section" : "Hide this section"}
                    className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-sm border ${it.hidden ? "border-slate-300 text-slate-400" : "border-emerald-300 text-emerald-700 bg-emerald-50"}`}
                  >
                    {it.hidden ? <><EyeSlash size={12}/> Hidden</> : <><Eye size={12}/> Visible</>}
                  </button>
                  <div className="flex flex-col">
                    <button type="button" disabled={disabled || i === 0} onClick={() => move(i, i - 1)} className="text-[10px] text-slate-500 hover:text-slate-900 disabled:opacity-30" aria-label="Move up">▲</button>
                    <button type="button" disabled={disabled || i === items.length - 1} onClick={() => move(i, i + 1)} className="text-[10px] text-slate-500 hover:text-slate-900 disabled:opacity-30" aria-label="Move down">▼</button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
            <button type="button" onClick={restoreDefaults} disabled={disabled || saving} data-testid="section-arranger-defaults" className="text-xs text-slate-600 hover:text-slate-900 underline disabled:opacity-50">
              Restore defaults
            </button>
            <div className="flex gap-2">
              {dirty && <button type="button" onClick={resetOrder} data-testid="section-arranger-cancel" className="text-xs px-3 py-1.5 border border-slate-300 rounded-sm hover:bg-slate-100">Cancel</button>}
              <button type="button" onClick={save} disabled={!dirty || saving || disabled} data-testid="section-arranger-save" className="text-xs px-3 py-1.5 bg-slate-900 hover:bg-blue-700 text-white rounded-sm transition-colors duration-200 disabled:opacity-50">
                {saving ? "Saving…" : "Save arrangement"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const PolishableTextarea = ({ initialValue, onCommit, section, clientName, rows = 5, disabled, testid }) => {
  const [value, setValue] = React.useState(initialValue || "");
  const [busy, setBusy] = React.useState(false);
  const [suggestion, setSuggestion] = React.useState(null);
  React.useEffect(() => { setValue(initialValue || ""); }, [initialValue]);

  const runPolish = async (currentValue) => {
    const trimmed = (currentValue || "").trim();
    if (trimmed.length < 8) return;
    setBusy(true); setSuggestion(null);
    try {
      const { data } = await api.post("/ai/polish", { text: currentValue, section, client_name: clientName || "" });
      const cleaned = (data?.cleaned_output || "").trim();
      if (cleaned && cleaned !== trimmed) setSuggestion(cleaned);
    } catch (e) {
      // silent — polish is best-effort
    } finally { setBusy(false); }
  };

  const handleBlur = async (e) => {
    const newVal = e.target.value;
    if (newVal !== (initialValue || "")) await onCommit(newVal);
    if (!disabled) runPolish(newVal);
  };

  return (
    <div>
      <div className="flex items-center justify-end">
        <span className="text-[10px] text-blue-600 font-semibold">AI-polish on blur</span>
      </div>
      <textarea rows={rows} value={value} disabled={disabled}
                onChange={(e) => setValue(e.target.value)}
                onBlur={handleBlur}
                className="mt-1 w-full border border-slate-200 rounded-sm p-3 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50 disabled:cursor-not-allowed"
                data-testid={testid} />
      {busy && <div className="mt-2 text-[11px] text-slate-500 italic" data-testid={`${testid}-polish-busy`}>✨ Polishing with AI…</div>}
      {suggestion && (
        <div className="mt-2 border border-blue-300 bg-blue-50 rounded-sm p-3 text-xs" data-testid={`${testid}-polish-suggestion`}>
          <div className="label-micro mb-1 text-blue-800">AI polish suggestion</div>
          <div className="text-slate-800 whitespace-pre-wrap leading-relaxed max-h-40 overflow-y-auto">{suggestion}</div>
          <div className="mt-2 flex gap-2">
            <button type="button" data-testid={`${testid}-polish-apply`}
                    onClick={async () => { setValue(suggestion); await onCommit(suggestion); setSuggestion(null); toast.success("Polished text applied"); }}
                    className="text-xs inline-flex items-center gap-1 bg-slate-900 hover:bg-blue-700 text-white rounded-sm px-2.5 py-1 transition-colors">Apply</button>
            <button type="button" data-testid={`${testid}-polish-keep`}
                    onClick={() => setSuggestion(null)}
                    className="text-xs inline-flex items-center gap-1 border border-slate-300 rounded-sm px-2.5 py-1 hover:bg-slate-100">Keep original</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default function ProposalView() {
  const { id } = useParams();
  const [prop, setProp] = useState(null);
  const [validation, setValidation] = useState(null);
  const [pricingTotals, setPricingTotals] = useState(null);
  const [aiBusy, setAiBusy] = useState({});
  const [editing, setEditing] = useState({});
  const [hubspotStatus, setHubspotStatus] = useState(null);
  const [hsBusy, setHsBusy] = useState(false);
  const [hsMenuOpen, setHsMenuOpen] = useState(false);
  const [features, setFeatures] = useState([]);
  const [priceBook, setPriceBook] = useState([]);
  const [approvers, setApprovers] = useState([]);
  const [assumptionInput, setAssumptionInput] = useState("");
  const [exclusionInput, setExclusionInput] = useState("");
  const [pricingBusy, setPricingBusy] = useState(false);
  const [pricingDraft, setPricingDraft] = useState(null); // null = view mode; array = edit mode
  const [submitPrev, setSubmitPrev] = useState(null); // {recipients, subject, note, busy}
  const [rejectPrev, setRejectPrev] = useState(null); // {reason, busy}
  const [clientSignBusy, setClientSignBusy] = useState(false);

  const reload = async () => {
    const [pr, v] = await Promise.all([api.get(`/proposals/${id}`), api.get(`/proposals/${id}/validate`)]);
    setProp(pr.data);
    setValidation(v.data);
  };

  useEffect(() => { reload(); }, [id]);

  useEffect(() => {
    Promise.all([
      api.get("/library/features?vertical=retail"),
      api.get("/library/price_book"),
      api.get("/users/approvers"),
    ]).then(([f, p, a]) => { setFeatures(f.data); setPriceBook(p.data); setApprovers(a.data || []); }).catch(() => { /* non-fatal */ });
  }, []);

  useEffect(() => {
    api.get("/integrations/hubspot").then(({ data }) => setHubspotStatus(data)).catch(() => setHubspotStatus({ configured: false }));
  }, []);

  const pushToHubSpot = async (fmt) => {
    setHsBusy(true); setHsMenuOpen(false);
    try {
      const { data } = await api.post(`/proposals/${id}/hubspot/push`, { format: fmt });
      toast.success(`Pushed to HubSpot · Deal #${data.deal_id}`);
      reload();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "HubSpot push failed"));
    } finally { setHsBusy(false); }
  };

  const savePatch = async (patch) => {
    try {
      const { data } = await api.put(`/proposals/${id}`, patch);
      setProp(data);
      toast.success("Saved");
      const v = await api.get(`/proposals/${id}/validate`);
      setValidation(v.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const draft = async (section) => {
    setAiBusy(s => ({...s, [section]:true}));
    try {
      const { data } = await api.post(`/proposals/${id}/ai/draft`, { section });
      toast.success(`AI drafted ${section}`);
      if (data.warnings?.length) data.warnings.forEach(w => toast.warning(w));
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "AI draft failed"); }
    finally { setAiBusy(s => ({...s, [section]:false})); }
  };

  const applyDraft = async (draft_id, action, text = "") => {
    try {
      await api.post(`/proposals/${id}/ai/apply`, { draft_id, action, text });
      toast.success(`Draft ${action}ed`);
      reload();
    } catch (e) { toast.error("Failed"); }
  };

  const openSubmitPreview = async () => {
    setSubmitPrev({ mode: "submit", note: "", busy: true, recipients: [], subject: "", fallback: false });
    try {
      const { data } = await api.post(`/proposals/${id}/submit/preview`, { note: "" });
      setSubmitPrev({ mode: "submit", note: "", busy: false,
        recipients: data.recipients || [], subject: data.subject || "",
        fallback: !!data.fallback_to_all_approvers });
    } catch (e) {
      toast.error("Failed to build preview");
      setSubmitPrev(null);
    }
  };

  const refreshPreviewNote = async (nextNote) => {
    if (!submitPrev || submitPrev.mode !== "submit") return;
    setSubmitPrev({ ...submitPrev, note: nextNote });
  };

  const confirmSubmit = async () => {
    setSubmitPrev({ ...submitPrev, busy: true });
    try {
      const { data } = await api.post(`/proposals/${id}/submit`, { note: submitPrev.note || "" });
      toast.success(`Submitted — ${data.notified_count || 0} approver(s) notified, ${data.emails_sent || 0} email(s) sent`);
      setSubmitPrev(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Blocked by validation");
      setSubmitPrev({ ...submitPrev, busy: false });
    }
  };

  const confirmApprove = async () => {
    setSubmitPrev({ ...submitPrev, busy: true });
    try {
      const { data } = await api.post(`/proposals/${id}/approve`, { note: submitPrev.note || "" });
      toast.success(`Approved — ${data.emails_sent || 0} email sent to submitter`);
      setSubmitPrev(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
      setSubmitPrev({ ...submitPrev, busy: false });
    }
  };

  const confirmReject = async () => {
    setRejectPrev({ ...rejectPrev, busy: true });
    try {
      const { data } = await api.post(`/proposals/${id}/reject`, { reason: rejectPrev.reason || "" });
      toast.info(`Sent back to draft — ${data.emails_sent || 0} email sent to submitter`);
      setRejectPrev(null);
      reload();
    } catch (e) {
      toast.error("Failed");
      setRejectPrev({ ...rejectPrev, busy: false });
    }
  };

  const generateClientSignLink = async () => {
    setClientSignBusy(true);
    try {
      const { data } = await api.post(`/proposals/${id}/client_sign/generate`);
      const link = `${window.location.origin}/sign/${data.token}`;
      try {
        await navigator.clipboard.writeText(link);
        toast.success("Signing link copied to clipboard");
      } catch (e) {
        toast.info(`Signing link ready: ${link}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate link");
    } finally { setClientSignBusy(false); }
  };

  const revokeClientSignLink = async () => {
    if (!window.confirm("Revoke the current signing link? Anyone with the old link won't be able to sign.")) return;
    try {
      await api.delete(`/proposals/${id}/client_sign/generate`);
      toast.success("Link revoked");
      reload();
    } catch (e) { toast.error("Failed"); }
  };

  const exportFile = async (fmt) => {
    try {
      const token = localStorage.getItem("sakshi_token");
      const res = await fetch(`${API}/proposals/${id}/export.${fmt}`, { headers: { Authorization: `Bearer ${token}` }});
      if (!res.ok) { const j = await res.json().catch(()=>({detail:"Failed"})); throw new Error(j.detail?.message || j.detail || "Export blocked"); }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `${prop.client_legal_name||"proposal"}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success(`${fmt.toUpperCase()} exported`);
      reload();
    } catch (e) { toast.error(String(e.message||e)); }
  };

  const totals = useMemo(() => {
    if (!prop?.pricing_lines) return null;
    const one = prop.pricing_lines.filter(l => l.charge_type==="one_time").reduce((a,b)=>a+b.total_amount,0);
    const rec = prop.pricing_lines.filter(l => l.charge_type==="recurring").reduce((a,b)=>a+b.total_amount,0);
    return { one, rec, yearOne: one + rec*12 };
  }, [prop]);

  const selectedFeatureObjs = useMemo(() => features.filter(f => (prop?.selected_feature_ids || []).includes(f.id)), [features, prop?.selected_feature_ids]);
  const requiredDeps = useMemo(() => {
    const items = [];
    for (const f of selectedFeatureObjs) for (const d of (f.dependencies || [])) items.push({ feature: f, dep: d });
    return items;
  }, [selectedFeatureObjs]);
  const findDepAnswer = (fid, type) => (prop?.dependency_answers || []).find(d => d.feature_id === fid && d.dependency_type === type);

  const toggleFeature = (fid) => {
    const cur = prop.selected_feature_ids || [];
    const next = cur.includes(fid) ? cur.filter(x => x !== fid) : [...cur, fid];
    savePatch({ selected_feature_ids: next });
  };

  const addSiteInline = () => {
    const next = [...(prop.sites || []), { id: crypto.randomUUID(), site_name: "", site_code: "", address: "", camera_count: 4, format_or_category: "", deployment_wave: 1, audit_status: "not_started" }];
    savePatch({ sites: next });
  };
  const updateSiteInline = (idx, patch) => {
    const next = (prop.sites || []).map((s, i) => i === idx ? { ...s, ...patch } : s);
    savePatch({ sites: next });
  };
  const removeSiteInline = (idx) => savePatch({ sites: (prop.sites || []).filter((_, i) => i !== idx) });

  const setDepAnswer = (fid, type, answer, notes = "") => {
    const others = (prop.dependency_answers || []).filter(d => !(d.feature_id === fid && d.dependency_type === type));
    savePatch({ dependency_answers: [...others, { feature_id: fid, dependency_type: type, answer, notes }] });
  };

  const addCriterion = () => savePatch({ success_criteria: [...(prop.success_criteria || []), { id: crypto.randomUUID(), metric: "", target: "", measurement_method: "" }] });
  const updateCriterion = (idx, patch) => savePatch({ success_criteria: (prop.success_criteria || []).map((c, i) => i === idx ? { ...c, ...patch } : c) });
  const removeCriterion = (idx) => savePatch({ success_criteria: (prop.success_criteria || []).filter((_, i) => i !== idx) });

  const addAssumption = () => {
    const t = assumptionInput.trim();
    if (!t) return;
    savePatch({ assumptions: [...(prop.assumptions || []), t] });
    setAssumptionInput("");
  };
  const removeAssumption = (idx) => savePatch({ assumptions: (prop.assumptions || []).filter((_, i) => i !== idx) });

  const addExclusion = () => {
    const t = exclusionInput.trim();
    if (!t) return;
    savePatch({ exclusions_ack: [...(prop.exclusions_ack || []), t] });
    setExclusionInput("");
  };
  const removeExclusion = (idx) => savePatch({ exclusions_ack: (prop.exclusions_ack || []).filter((_, i) => i !== idx) });

  const startPricingEdit = () => {
    const lines = (prop.pricing_lines || []).map(l => ({
      charge_code: l.charge_code, mode: l.mode, quantity: l.quantity,
      unit_price_override: l.mode === "Standard" ? undefined : l.unit_price,
      justification: l.justification || "", label_override: null,
    }));
    setPricingDraft(lines);
  };
  const cancelPricingEdit = () => setPricingDraft(null);
  const updatePricingDraft = (idx, patch) => setPricingDraft(pricingDraft.map((l, i) => i === idx ? { ...l, ...patch } : l));
  const removePricingDraft = (idx) => setPricingDraft(pricingDraft.filter((_, i) => i !== idx));
  const addPricingDraft = (charge_code) => {
    if (pricingDraft.find(l => l.charge_code === charge_code)) return;
    const pb = priceBook.find(p => p.charge_code === charge_code);
    const totalCameras = (prop.sites || []).reduce((a, s) => a + Number(s.camera_count || 0), 0);
    const totalSites = (prop.sites || []).length;
    const qty = pb?.basis === "per_camera" ? Math.max(1, totalCameras) : pb?.basis === "per_site" ? Math.max(1, totalSites) : 1;
    setPricingDraft([...pricingDraft, { charge_code, mode: "Standard", quantity: qty, justification: "" }]);
  };
  const [flashTarget, setFlashTarget] = useState("");

  const CODE_TO_SECTION = {
    B1_CLIENT_NAME: { id: "section-deal-basics", focus: "edit-client-name" },
    B2_RECIPIENT: { id: "section-deal-basics", focus: "edit-recipient-name" },
    B3_RECIPIENT_DESIGNATION: { id: "section-deal-basics", focus: "edit-recipient-designation" },
    B4_FEATURES: { id: "section-features-edit" },
    B5_SITES: { id: "section-sites-edit" },
    B6_EXECUTIVE_SUMMARY: { id: "section-exec_summary" },
    B6_UNDERSTANDING: { id: "section-understanding" },
    B7_PILOT_OUTCOME: { id: "section-pilot_outcome" },
    B8_POST_PILOT: { id: "section-deal-basics", focus: "edit-post-pilot" },
    B9_SUCCESS_METRICS: { id: "section-success-criteria" },
    B10_DEPENDENCY: { id: "section-dependencies-edit" },
    B11_PRICING_MISSING: { id: "section-pricing-edit" },
    B12_COMMERCIAL_TERMS: { id: "section-deal-basics", focus: "edit-commercial-terms" },
    B14_VALIDITY: { id: "section-deal-basics", focus: "edit-validity" },
    W1_HUBSPOT: { id: "section-deal-basics", focus: "edit-hubspot" },
    W2_ASSUMPTIONS: { id: "section-assumptions-exclusions", focus: "assumption-input" },
    W4_EXCLUSIONS_ACK: { id: "section-assumptions-exclusions", focus: "exclusion-input" },
  };

  const jumpToCode = (code) => {
    let target = CODE_TO_SECTION[code];
    if (!target && code.startsWith("B13_PLACEHOLDER_")) {
      const field = code.replace("B13_PLACEHOLDER_", "").toLowerCase();
      target = { id: `section-${field}` };
    }
    if (!target) return;
    const el = document.querySelector(`[data-testid="${target.id}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    setFlashTarget(target.id);
    setTimeout(() => setFlashTarget(""), 1800);
    if (target.focus) {
      setTimeout(() => {
        const f = document.querySelector(`[data-testid="${target.focus}"]`);
        if (f && typeof f.focus === "function") f.focus();
      }, 400);
    }
  };

  useEffect(() => {
    if (!flashTarget) return;
    const el = document.querySelector(`[data-testid="${flashTarget}"]`);
    if (!el) return;
    const cls = ["ring-2", "ring-blue-500", "ring-offset-2", "transition-shadow"];
    el.classList.add(...cls);
    const t = setTimeout(() => el.classList.remove(...cls), 1800);
    return () => { clearTimeout(t); el.classList.remove(...cls); };
  }, [flashTarget]);

  const savePricing = async () => {
    setPricingBusy(true);
    try {
      const payload = pricingDraft.map(l => ({
        charge_code: l.charge_code,
        mode: l.mode,
        quantity: Number(l.quantity) || 1,
        unit_price_override: l.mode !== "Standard" ? Number(l.unit_price_override) || undefined : undefined,
        justification: l.justification || "",
      }));
      const { data } = await api.post(`/proposals/${id}/pricing/compute`, payload);
      if (Array.isArray(data?.errors) && data.errors.length) data.errors.forEach(e => toast.warning(String(e)));
      toast.success("Pricing recomputed");
      setPricingDraft(null);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Recompute failed"); }
    finally { setPricingBusy(false); }
  };

  if (!prop) return <div className="p-8 text-sm text-slate-500">Loading…</div>;

  const isReadOnly = prop.status === "issued" || prop.status === "superseded";

  const draftsBySection = (section) => (prop.ai_drafts || []).filter(d => d.section === section);

  const POLISH_SECTIONS = { exec_summary: "executive_summary", understanding: "understanding", pilot_outcome: "pilot_outcome", site_schedule_notes: "site_schedule_notes" };

  const AISection = ({ sectionKey, label, field }) => (
    <div className="bg-white border border-slate-200 p-5" data-testid={`section-${sectionKey}`}>
      <div className="flex items-center justify-between">
        <h3 className="font-display font-bold">{label}</h3>
        <button onClick={() => draft(sectionKey)} disabled={aiBusy[sectionKey] || (prop.selected_feature_ids||[]).length===0 || isReadOnly}
                className="text-xs inline-flex items-center gap-1 border border-slate-300 rounded-sm px-3 py-1.5 hover:bg-slate-900 hover:text-white transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid={`ai-draft-${sectionKey}`}>
          <Sparkle size={12}/> {aiBusy[sectionKey] ? "Drafting…" : "AI draft"}
        </button>
      </div>
      <PolishableTextarea
        key={`${field}-${prop.updated_at || prop.id}`}
        initialValue={prop[field] || ""}
        onCommit={(v) => savePatch({ [field]: v })}
        section={POLISH_SECTIONS[sectionKey] || sectionKey}
        clientName={prop.client_legal_name}
        rows={5}
        disabled={isReadOnly}
        testid={`textarea-${field}`}
      />
      {draftsBySection(sectionKey).filter(d => d.status === "draft").map(d => (
        <div key={d.id} className="mt-3 border-l-2 border-blue-500 bg-blue-50/40 p-3">
          <div className="flex items-center justify-between mb-2"><span className="label-micro">AI suggestion · {d.model_id}</span>
            <div className="flex gap-2">
              <button onClick={() => applyDraft(d.id, "accept", d.accepted_text)} className="text-xs inline-flex items-center gap-1 bg-emerald-600 text-white px-2 py-1 rounded-sm hover:bg-emerald-700" data-testid={`ai-accept-${d.id}`}><Check size={12}/>Accept</button>
              <button onClick={() => applyDraft(d.id, "reject")} className="text-xs inline-flex items-center gap-1 bg-red-600 text-white px-2 py-1 rounded-sm hover:bg-red-700" data-testid={`ai-reject-${d.id}`}><X size={12}/>Reject</button>
            </div>
          </div>
          <textarea defaultValue={d.accepted_text} rows={5} onBlur={(e) => applyDraft(d.id, "edit", e.target.value)}
                    className="w-full bg-white border border-slate-200 rounded-sm p-2 text-sm" data-testid={`ai-edit-${d.id}`}/>
        </div>
      ))}
    </div>
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px]" data-testid="proposal-view">
      <div className="mb-4">
        <Link to="/" data-testid="btn-back-dashboard" className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900">
          <ArrowLeft size={14}/> Back to dashboard
        </Link>
      </div>

      {isReadOnly && (
        <div className="mb-4 border-l-4 border-slate-900 bg-slate-100 px-4 py-3 rounded-sm flex items-start gap-3" data-testid="readonly-banner">
          <Lock size={18} className="text-slate-700 mt-0.5" weight="fill"/>
          <div>
            <div className="font-semibold text-slate-900 text-sm">This proposal is {prop.status} and locked for editing.</div>
            <div className="text-xs text-slate-600 mt-0.5">Once a proposal is exported it becomes immutable to protect the sent document. To make changes, create a new proposal (or clone this one).</div>
          </div>
        </div>
      )}

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="label-micro">{prop.proposal_type} proposal</div>
          <h1 className="font-display text-2xl sm:text-3xl font-black mt-1 break-words">{prop.client_legal_name}</h1>
          <div className="text-xs sm:text-sm text-slate-500 mt-1">Prepared by {prop.prepared_by_name} · Recipient {prop.recipient_name} ({prop.recipient_designation})</div>
          <div className="mt-3"><span className={`status-badge status-${prop.status}`} data-testid="status-badge">{prop.status.replace('_',' ')}</span></div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to={`/proposals/${id}/preview`} data-testid="btn-preview" className="text-sm inline-flex items-center gap-2 border border-slate-300 rounded-sm px-3 py-2 hover:bg-slate-100"><Eye size={14}/>Preview</Link>
          {(prop.status === "approved" || prop.status === "issued") && (
            <button onClick={generateClientSignLink} disabled={clientSignBusy} data-testid="btn-client-sign-link"
                    className="text-sm inline-flex items-center gap-2 border border-emerald-500 text-emerald-700 hover:bg-emerald-500 hover:text-white rounded-sm px-3 py-2 transition-colors duration-200 disabled:opacity-60">
              <Signature size={14}/>{clientSignBusy ? "…" : (prop.client_sign_token ? "Copy client link" : "Get client signing link")}
            </button>
          )}
          {prop.status === "draft" && <button onClick={openSubmitPreview} data-testid="btn-submit" className="text-sm inline-flex items-center gap-2 border border-slate-300 rounded-sm px-3 py-2 hover:bg-slate-100"><PaperPlaneTilt size={14}/>Submit</button>}
          {prop.status === "pending_approval" && (
            <>
              <button onClick={() => setSubmitPrev({ mode: "approve", note: "" })} data-testid="btn-approve" className="text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm px-3 py-2">Approve</button>
              <button onClick={() => setRejectPrev({ reason: "" })} data-testid="btn-reject" className="text-sm border border-slate-300 rounded-sm px-3 py-2 hover:bg-slate-100">Reject</button>
            </>
          )}
          <button onClick={() => exportFile("docx")} data-testid="btn-export-docx" className="text-sm inline-flex items-center gap-2 bg-slate-900 hover:bg-blue-700 text-white rounded-sm px-3 py-2 transition-colors duration-200"><DownloadSimple size={14}/>DOCX</button>
          <button onClick={() => exportFile("pdf")} data-testid="btn-export-pdf" className="text-sm inline-flex items-center gap-2 border border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white rounded-sm px-3 py-2 transition-colors duration-200"><DownloadSimple size={14}/>PDF</button>
          {hubspotStatus?.configured && (prop.hubspot_contact_id || prop.hubspot_company_id || prop.hubspot_deal_id) && (
            <div className="relative">
              <button onClick={() => setHsMenuOpen(v => !v)} disabled={hsBusy} data-testid="btn-hubspot-push"
                      className="text-sm inline-flex items-center gap-2 border border-orange-500 text-orange-700 hover:bg-orange-500 hover:text-white rounded-sm px-3 py-2 transition-colors duration-200 disabled:opacity-60">
                <Plug size={14} weight="fill"/>{hsBusy ? "Pushing…" : "Push to HubSpot"}<CaretDown size={12}/>
              </button>
              {hsMenuOpen && !hsBusy && (
                <div className="absolute right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-lg z-30 min-w-[180px]" data-testid="hs-push-menu">
                  <button onClick={() => pushToHubSpot("docx")} data-testid="hs-push-docx" className="w-full text-left px-3 py-2 text-sm hover:bg-slate-100">Push as DOCX</button>
                  <button onClick={() => pushToHubSpot("pdf")} data-testid="hs-push-pdf" className="w-full text-left px-3 py-2 text-sm hover:bg-slate-100 border-t border-slate-100">Push as PDF</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 space-y-4 min-w-0">
          <SectionArranger proposalId={id} disabled={isReadOnly} />
          <div className="bg-white border border-slate-200 p-5" data-testid="section-deal-basics">
            <h3 className="font-display font-bold">Deal Basics</h3>
            <p className="text-xs text-slate-500 mt-1">Fix any missing mandatory fields here — the Validation panel updates on save.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              <label className="block"><span className="label-micro">Client legal name *</span>
                <input defaultValue={prop.client_legal_name || ""} disabled={isReadOnly}
                  onBlur={(e) => e.target.value !== (prop.client_legal_name || "") && savePatch({ client_legal_name: e.target.value })}
                  className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50 disabled:cursor-not-allowed"
                  data-testid="edit-client-name"/></label>
              <label className="block"><span className="label-micro">Recipient name *</span>
                <input defaultValue={prop.recipient_name || ""} disabled={isReadOnly}
                  onBlur={(e) => e.target.value !== (prop.recipient_name || "") && savePatch({ recipient_name: e.target.value })}
                  className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50 disabled:cursor-not-allowed"
                  data-testid="edit-recipient-name"/></label>
              <label className="block"><span className="label-micro">Recipient designation *</span>
                <input defaultValue={prop.recipient_designation || ""} disabled={isReadOnly}
                  onBlur={(e) => e.target.value !== (prop.recipient_designation || "") && savePatch({ recipient_designation: e.target.value })}
                  className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50 disabled:cursor-not-allowed"
                  data-testid="edit-recipient-designation"/></label>
              <label className="block"><span className="label-micro">Validity (days) *</span>
                <input type="number" defaultValue={prop.validity_days || 30} disabled={isReadOnly}
                  onBlur={(e) => Number(e.target.value) !== prop.validity_days && savePatch({ validity_days: Number(e.target.value) || 30 })}
                  className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50 disabled:cursor-not-allowed"
                  data-testid="edit-validity"/></label>
              <label className="block"><span className="label-micro">HubSpot deal ID</span>
                <input defaultValue={prop.hubspot_deal_id || ""} disabled={isReadOnly}
                  onBlur={(e) => e.target.value !== (prop.hubspot_deal_id || "") && savePatch({ hubspot_deal_id: e.target.value || null })}
                  className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50 disabled:cursor-not-allowed"
                  data-testid="edit-hubspot"/></label>
              <label className="block"><span className="label-micro">Deployment model</span>
                <select defaultValue={prop.deployment_model} disabled={isReadOnly}
                  onChange={(e) => savePatch({ deployment_model: e.target.value })}
                  className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm disabled:bg-slate-50 disabled:cursor-not-allowed"
                  data-testid="edit-deployment">
                  <option value="on_prem_edge">On-prem / Edge</option><option value="remote_stream">Remote Stream</option>
                </select></label>
              <label className="block"><span className="label-micro">Tax treatment</span>
                <select defaultValue={prop.tax_treatment} disabled={isReadOnly}
                  onChange={(e) => savePatch({ tax_treatment: e.target.value })}
                  className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm disabled:bg-slate-50 disabled:cursor-not-allowed"
                  data-testid="edit-tax">
                  <option value="exclusive_gst">Exclusive of GST</option><option value="inclusive_gst">Inclusive of GST</option>
                </select></label>
            </div>
            {prop.proposal_type === "commercial" && (
              <div className="mt-4">
                <span className="label-micro">Commercial terms *</span>
                <PolishableTextarea
                  key={`commercial_terms-${prop.updated_at || prop.id}`}
                  initialValue={prop.commercial_terms || ""}
                  onCommit={(v) => savePatch({ commercial_terms: v })}
                  section="commercial_terms"
                  clientName={prop.client_legal_name}
                  rows={4}
                  disabled={isReadOnly}
                  testid="edit-commercial-terms"
                />
              </div>
            )}
            {prop.proposal_type === "pilot" && (
              <div className="mt-4">
                <span className="label-micro">Post-pilot pathway *</span>
                <PolishableTextarea
                  key={`post_pilot_pathway-${prop.updated_at || prop.id}`}
                  initialValue={prop.post_pilot_pathway || ""}
                  onCommit={(v) => savePatch({ post_pilot_pathway: v })}
                  section="post_pilot_pathway"
                  clientName={prop.client_legal_name}
                  rows={3}
                  disabled={isReadOnly}
                  testid="edit-post-pilot"
                />
              </div>
            )}
          </div>

          <AISection sectionKey="exec_summary" label="1. Executive Summary" field="executive_summary" />
          <AISection sectionKey="understanding" label="2. Understanding of Requirement" field="understanding" />
          {prop.proposal_type === "pilot" && <AISection sectionKey="pilot_outcome" label="Pilot Outcome" field="pilot_outcome" />}
          <AISection sectionKey="site_schedule_notes" label="Site Schedule Notes" field="site_schedule_notes" />

          {/* Sites */}
          <div className="bg-white border border-slate-200 p-5" data-testid="section-sites-edit">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-bold">Sites <span className="label-micro ml-2">{(prop.sites||[]).length}</span></h3>
              <button onClick={addSiteInline} disabled={isReadOnly} data-testid="btn-add-site"
                      className="text-xs inline-flex items-center gap-1 border border-slate-300 rounded-sm px-3 py-1.5 hover:bg-slate-900 hover:text-white transition-colors duration-200 disabled:opacity-50">
                <Plus size={12}/> Add site
              </button>
            </div>
            {(prop.sites || []).length === 0 && <div className="text-sm text-slate-500 border-l-2 border-red-500 pl-3 py-2 bg-red-50">No sites yet — add at least one to enable export.</div>}
            <div className="space-y-3">
              {(prop.sites || []).map((s, i) => (
                <div key={s.id || i} className="border border-slate-200 rounded-sm p-3" data-testid={`site-row-${i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="label-micro">Site {i + 1}</div>
                    <button onClick={() => removeSiteInline(i)} disabled={isReadOnly} className="text-xs text-red-600 hover:underline disabled:opacity-50" data-testid={`site-remove-${i}`}>Remove</button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    <label className="block"><span className="label-micro">Site name</span>
                      <input defaultValue={s.site_name} disabled={isReadOnly} data-testid={`site-name-${i}`}
                             onBlur={(e) => e.target.value !== s.site_name && updateSiteInline(i, { site_name: e.target.value })}
                             className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5 text-sm"/></label>
                    <label className="block"><span className="label-micro">Site code</span>
                      <input defaultValue={s.site_code} disabled={isReadOnly} data-testid={`site-code-${i}`}
                             onBlur={(e) => e.target.value !== s.site_code && updateSiteInline(i, { site_code: e.target.value })}
                             className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5 text-sm"/></label>
                    <label className="block"><span className="label-micro">Cameras</span>
                      <input type="number" defaultValue={s.camera_count} disabled={isReadOnly} data-testid={`site-cams-${i}`}
                             onBlur={(e) => Number(e.target.value) !== s.camera_count && updateSiteInline(i, { camera_count: Number(e.target.value) })}
                             className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5 text-sm"/></label>
                    <label className="block"><span className="label-micro">Wave</span>
                      <input type="number" defaultValue={s.deployment_wave} disabled={isReadOnly} data-testid={`site-wave-${i}`}
                             onBlur={(e) => Number(e.target.value) !== s.deployment_wave && updateSiteInline(i, { deployment_wave: Number(e.target.value) })}
                             className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5 text-sm"/></label>
                    <label className="block sm:col-span-2 lg:col-span-3"><span className="label-micro">Address</span>
                      <input defaultValue={s.address} disabled={isReadOnly} data-testid={`site-addr-${i}`}
                             onBlur={(e) => e.target.value !== s.address && updateSiteInline(i, { address: e.target.value })}
                             className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5 text-sm"/></label>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Features */}
          <div className="bg-white border border-slate-200 p-5" data-testid="section-features-edit">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-bold">Features <span className="label-micro ml-2">{(prop.selected_feature_ids||[]).length} selected</span></h3>
              <Link to="/library" className="text-xs text-blue-600 hover:underline">Manage library →</Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {features.map(f => {
                const on = (prop.selected_feature_ids || []).includes(f.id);
                return (
                  <button key={f.id} type="button" onClick={() => !isReadOnly && toggleFeature(f.id)} disabled={isReadOnly}
                          data-testid={`feature-toggle-${f.name.toLowerCase().replace(/\s/g,'-')}`}
                          className={`text-left border rounded-sm p-3 transition-colors ${on ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white hover:border-blue-400"} disabled:opacity-60 disabled:cursor-not-allowed`}>
                    <div className="flex items-start gap-2">
                      <input type="checkbox" checked={on} readOnly className="mt-1"/>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-slate-900 truncate">{f.name} <span className="label-micro ml-1">{f.module}</span></div>
                        <div className="text-xs text-slate-600 mt-1 line-clamp-2">{f.client_outcome}</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Dependencies */}
          {requiredDeps.length > 0 && (
            <div className="bg-white border border-slate-200 p-5" data-testid="section-dependencies-edit">
              <h3 className="font-display font-bold mb-2">Feature Dependencies</h3>
              <p className="text-xs text-slate-500 mb-3">Answer each dependency question — export is blocked until every one has an answer.</p>
              <div className="space-y-3">
                {requiredDeps.map(({feature, dep}) => {
                  const ans = findDepAnswer(feature.id, dep.type);
                  return (
                    <div key={feature.id + dep.type} className="border border-slate-200 rounded-sm p-3" data-testid={`dep-row-${feature.id}-${dep.type}`}>
                      <div className="text-sm"><span className="font-semibold">{feature.name}</span> · <span className="text-slate-600">{dep.description}</span></div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {["yes","no","n/a"].map(a => (
                          <button key={a} onClick={() => !isReadOnly && setDepAnswer(feature.id, dep.type, a, ans?.notes || "")} disabled={isReadOnly}
                                  className={`px-3 py-1.5 text-xs font-semibold rounded-sm border transition-colors ${ans?.answer === a ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 hover:border-blue-500"} disabled:opacity-50`}
                                  data-testid={`dep-btn-${feature.id}-${dep.type}-${a}`}>{a.toUpperCase()}</button>
                        ))}
                      </div>
                      <label className="block mt-2"><span className="label-micro">Notes</span>
                        <input defaultValue={ans?.notes || ""} disabled={isReadOnly}
                               onBlur={(e) => e.target.value !== (ans?.notes || "") && setDepAnswer(feature.id, dep.type, ans?.answer || "n/a", e.target.value)}
                               className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5 text-sm"/></label>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Success Criteria (pilot) */}
          {prop.proposal_type === "pilot" && (
            <div className="bg-white border border-slate-200 p-5" data-testid="section-success-criteria">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display font-bold">Success Criteria <span className="label-micro ml-2">{(prop.success_criteria||[]).length}</span></h3>
                <button onClick={addCriterion} disabled={isReadOnly} data-testid="btn-add-criterion" className="text-xs inline-flex items-center gap-1 border border-slate-300 rounded-sm px-3 py-1.5 hover:bg-slate-900 hover:text-white transition-colors duration-200 disabled:opacity-50">
                  <Plus size={12}/> Add criterion
                </button>
              </div>
              {(prop.success_criteria || []).length === 0 && <div className="text-xs text-slate-500 border-l-2 border-red-500 pl-3 py-2 bg-red-50">Pilots require at least one measurable success criterion.</div>}
              <div className="space-y-2">
                {(prop.success_criteria || []).map((c, i) => (
                  <div key={c.id || i} className="grid grid-cols-1 sm:grid-cols-[1fr,1fr,1fr,auto] gap-2 items-end" data-testid={`sc-row-${i}`}>
                    <label className="block"><span className="label-micro">Metric</span>
                      <input defaultValue={c.metric} disabled={isReadOnly} data-testid={`sc-metric-${i}`}
                             onBlur={(e) => e.target.value !== c.metric && updateCriterion(i, { metric: e.target.value })}
                             className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5 text-sm"/></label>
                    <label className="block"><span className="label-micro">Target</span>
                      <input defaultValue={c.target} disabled={isReadOnly} data-testid={`sc-target-${i}`}
                             onBlur={(e) => e.target.value !== c.target && updateCriterion(i, { target: e.target.value })}
                             className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5 text-sm"/></label>
                    <label className="block"><span className="label-micro">Method</span>
                      <input defaultValue={c.measurement_method} disabled={isReadOnly} data-testid={`sc-method-${i}`}
                             onBlur={(e) => e.target.value !== c.measurement_method && updateCriterion(i, { measurement_method: e.target.value })}
                             className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5 text-sm"/></label>
                    <button onClick={() => removeCriterion(i)} disabled={isReadOnly} data-testid={`sc-remove-${i}`} className="text-xs text-red-600 hover:underline pb-2 disabled:opacity-50">Remove</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Assumptions + Exclusions */}
          <div className="bg-white border border-slate-200 p-5" data-testid="section-assumptions-exclusions">
            <h3 className="font-display font-bold">Assumptions & Exclusions</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-3">
              <div>
                <div className="label-micro mb-2">Assumptions</div>
                <ul className="space-y-1">{(prop.assumptions || []).map((a, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <span className="flex-1">• {a}</span>
                    <button onClick={() => removeAssumption(i)} disabled={isReadOnly} className="text-xs text-red-600 disabled:opacity-50" data-testid={`assumption-remove-${i}`}>Remove</button>
                  </li>
                ))}</ul>
                <div className="flex gap-2 mt-2">
                  <input value={assumptionInput} onChange={(e) => setAssumptionInput(e.target.value)} disabled={isReadOnly}
                         placeholder="e.g., Client provides POS access on day 1" data-testid="assumption-input"
                         className="flex-1 border border-slate-300 rounded-sm px-2 py-1.5 text-sm"/>
                  <button onClick={addAssumption} disabled={isReadOnly} data-testid="assumption-add" className="text-xs px-3 py-1.5 border border-slate-300 rounded-sm hover:bg-slate-100 disabled:opacity-50">Add</button>
                </div>
              </div>
              <div>
                <div className="label-micro mb-2">Exclusions acknowledged</div>
                <ul className="space-y-1">{(prop.exclusions_ack || []).map((a, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <span className="flex-1">• {a}</span>
                    <button onClick={() => removeExclusion(i)} disabled={isReadOnly} className="text-xs text-red-600 disabled:opacity-50" data-testid={`exclusion-remove-${i}`}>Remove</button>
                  </li>
                ))}</ul>
                <div className="flex gap-2 mt-2">
                  <input value={exclusionInput} onChange={(e) => setExclusionInput(e.target.value)} disabled={isReadOnly}
                         placeholder="e.g., Facial recognition explicitly out of scope" data-testid="exclusion-input"
                         className="flex-1 border border-slate-300 rounded-sm px-2 py-1.5 text-sm"/>
                  <button onClick={addExclusion} disabled={isReadOnly} data-testid="exclusion-add" className="text-xs px-3 py-1.5 border border-slate-300 rounded-sm hover:bg-slate-100 disabled:opacity-50">Add</button>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 p-5" data-testid="section-pricing-edit">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-bold">Pricing</h3>
              {!pricingDraft && !isReadOnly && (
                <button onClick={startPricingEdit} data-testid="btn-edit-pricing" className="text-xs inline-flex items-center gap-1 border border-slate-300 rounded-sm px-3 py-1.5 hover:bg-slate-900 hover:text-white transition-colors duration-200">
                  <Calculator size={12}/> Edit pricing
                </button>
              )}
            </div>
            {!pricingDraft && (prop.pricing_lines || []).length === 0 && <div className="text-sm text-slate-500">No pricing lines. Click <b>Edit pricing</b> to add them.</div>}
            {!pricingDraft && (prop.pricing_lines || []).length > 0 && (
              <>
                <div className="overflow-x-auto">
                <table className="w-full text-sm mt-3 min-w-[520px]">
                  <thead className="bg-slate-50 border-b border-slate-200"><tr className="text-left"><th className="px-3 py-2 label-micro">Line</th><th className="px-3 py-2 label-micro">Mode</th><th className="px-3 py-2 label-micro">Qty</th><th className="px-3 py-2 label-micro text-right">Unit</th><th className="px-3 py-2 label-micro text-right">Total</th></tr></thead>
                  <tbody>
                    {prop.pricing_lines.map((l, i) => (
                      <tr key={i} className="border-b border-slate-100">
                        <td className="px-3 py-2">{l.label} <span className="label-micro ml-2">{l.charge_type}</span></td>
                        <td className="px-3 py-2 text-xs">{l.mode}</td><td className="px-3 py-2 tabular-nums">{l.quantity}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmtINR(l.unit_price)}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold">{fmtINR(l.total_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
                {totals && (
                  <div className="mt-3 border-t border-slate-200 pt-3 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                    <div><div className="label-micro">One-time</div><div className="font-display text-xl font-bold tabular-nums">{fmtINR(totals.one)}</div></div>
                    <div><div className="label-micro">Recurring / month</div><div className="font-display text-xl font-bold tabular-nums">{fmtINR(totals.rec)}</div></div>
                    <div><div className="label-micro">Year-one total</div><div className="font-display text-xl font-bold tabular-nums text-blue-700">{fmtINR(totals.yearOne)}</div></div>
                  </div>
                )}
              </>
            )}
            {pricingDraft && (
              <div className="mt-1 space-y-3" data-testid="pricing-editor">
                <div className="text-xs text-slate-600 border-l-2 border-blue-500 bg-blue-50 pl-3 py-2">Editing pricing. Click <b>Recompute &amp; save</b> to apply — units and totals are recalculated on the backend against the price book.</div>
                <div>
                  <div className="label-micro mb-2">Add lines from price book</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {priceBook.map(p => {
                      const already = pricingDraft.find(l => l.charge_code === p.charge_code);
                      return (
                        <button key={p.id} onClick={() => addPricingDraft(p.charge_code)} disabled={!!already} data-testid={`pv-add-pline-${p.charge_code}`}
                                className={`text-left border rounded-sm px-3 py-2 transition-colors ${already ? "border-emerald-300 bg-emerald-50 cursor-default" : "border-slate-300 hover:border-blue-500 hover:bg-blue-50"}`}>
                          <div className="flex items-center justify-between gap-2">
                            <div className="text-sm font-semibold text-slate-900 truncate">{p.label}</div>
                            <div className="text-sm font-bold tabular-nums text-slate-900 shrink-0">₹{(p.list_price/100).toLocaleString("en-IN")}</div>
                          </div>
                          <div className="flex items-center justify-between mt-1">
                            <span className="label-micro">{p.charge_code} · {p.basis.replace(/_/g,' ')}</span>
                            <span className="text-xs text-slate-500">{already ? "✓ added" : "+ add"}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
                {pricingDraft.map((l, i) => {
                  const pb = priceBook.find(p => p.charge_code === l.charge_code);
                  const unitPaise = l.mode === "Standard" ? (pb?.list_price || 0) : (Number(l.unit_price_override) || (pb?.list_price || 0));
                  const subtotalPaise = unitPaise * (Number(l.quantity) || 1);
                  const belowFloor = pb && unitPaise < pb.floor_price;
                  return (
                    <div key={l.charge_code} className="border border-slate-200 bg-white p-3">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                          <div className="text-sm font-semibold">{pb?.label} <span className="label-micro ml-2">{l.charge_code}</span></div>
                          <div className="text-xs text-slate-500 mt-0.5">List: <b className="tabular-nums">{fmtINR(pb?.list_price || 0)}</b> · Floor: <b className="tabular-nums">{fmtINR(pb?.floor_price || 0)}</b></div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="label-micro">Subtotal</div>
                          <div className={`font-display text-xl font-bold tabular-nums ${belowFloor ? "text-red-600" : "text-slate-900"}`} data-testid={`pv-subtotal-${l.charge_code}`}>{fmtINR(subtotalPaise)}</div>
                        </div>
                        <button onClick={() => removePricingDraft(i)} className="text-xs text-red-600 hover:underline" data-testid={`pv-remove-${i}`}>Remove</button>
                      </div>
                      {belowFloor && <div className="mt-2 text-xs text-red-600 bg-red-50 border-l-2 border-red-500 px-2 py-1">Unit price is below floor. Use Custom mode with justification.</div>}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 mt-2">
                        <label><span className="label-micro">Mode</span>
                          <select value={l.mode} onChange={(e) => updatePricingDraft(i, { mode: e.target.value })} className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5 text-sm" data-testid={`pv-mode-${i}`}>
                            <option>Standard</option><option>Adjusted</option><option>Custom</option>
                          </select></label>
                        <label><span className="label-micro">Qty</span>
                          <input type="number" value={l.quantity} onChange={(e) => updatePricingDraft(i, { quantity: Number(e.target.value) })} className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5 text-sm" data-testid={`pv-qty-${i}`}/></label>
                        {l.mode !== "Standard" && <label><span className="label-micro">Unit (paise)</span>
                          <input type="number" value={l.unit_price_override || ""} onChange={(e) => updatePricingDraft(i, { unit_price_override: Number(e.target.value) })} className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5 text-sm" data-testid={`pv-unit-${i}`}/></label>}
                        {l.mode !== "Standard" && <label><span className="label-micro">Justification</span>
                          <input value={l.justification} onChange={(e) => updatePricingDraft(i, { justification: e.target.value })} className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5 text-sm" data-testid={`pv-just-${i}`}/></label>}
                      </div>
                    </div>
                  );
                })}
                <div className="flex gap-2 sticky bottom-0 bg-white py-2 border-t border-slate-100">
                  <button onClick={savePricing} disabled={pricingBusy} data-testid="btn-save-pricing" className="text-sm bg-slate-900 hover:bg-blue-700 text-white rounded-sm px-4 py-2 inline-flex items-center gap-2 transition-colors duration-200 disabled:opacity-60">
                    <Calculator size={14}/> {pricingBusy ? "Computing…" : "Recompute & save"}
                  </button>
                  <button onClick={cancelPricingEdit} disabled={pricingBusy} data-testid="btn-cancel-pricing" className="text-sm border border-slate-300 rounded-sm px-4 py-2 hover:bg-slate-100 disabled:opacity-60">Cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>

        <aside className="space-y-4">
          <div className="bg-white border border-slate-200 p-5" data-testid="validation-panel">
            <h3 className="font-display font-bold flex items-center gap-2">{validation?.can_export ? <CheckCircle size={18} className="text-emerald-500"/> : <WarningCircle size={18} className="text-red-500"/>} Validation</h3>
            <div className="mt-2 text-xs text-slate-500">{validation?.can_export ? "All BLOCK rules pass — proposal can be exported." : `${validation?.block?.length || 0} block · ${validation?.warn?.length || 0} warn`}</div>
            <div className="mt-3 space-y-2">
              {(validation?.block || []).map((b,i) => (
                <button key={i} type="button" onClick={() => jumpToCode(b.code)} data-testid={`block-${b.code}`}
                        className="w-full text-left border-l-2 border-red-500 bg-red-50 hover:bg-red-100 pl-3 pr-2 py-2 text-xs transition-colors group">
                  <div className="flex items-center justify-between">
                    <div className="font-mono text-[10px] text-red-700">{b.code}</div>
                    <span className="text-[10px] font-semibold text-red-700 opacity-0 group-hover:opacity-100 transition-opacity">Fix →</span>
                  </div>
                  <div className="text-slate-800 mt-0.5">{b.message}</div>
                </button>
              ))}
              {(validation?.warn || []).map((w,i) => (
                <button key={i} type="button" onClick={() => jumpToCode(w.code)} data-testid={`warn-${w.code}`}
                        className="w-full text-left border-l-2 border-amber-500 bg-amber-50 hover:bg-amber-100 pl-3 pr-2 py-2 text-xs transition-colors group">
                  <div className="flex items-center justify-between">
                    <div className="font-mono text-[10px] text-amber-700">{w.code}</div>
                    <span className="text-[10px] font-semibold text-amber-700 opacity-0 group-hover:opacity-100 transition-opacity">Fix →</span>
                  </div>
                  <div className="text-slate-800 mt-0.5">{w.message}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white border border-slate-200 p-5">
            <div className="label-micro">Snapshot</div>
            <div className="text-sm mt-2 space-y-1">
              <div>Features: <b>{(prop.selected_feature_ids||[]).length}</b></div>
              <div>Sites: <b>{(prop.sites||[]).length}</b></div>
              <div>Success criteria: <b>{(prop.success_criteria||[]).length}</b></div>
              <div>Validity: <b>{prop.validity_days} days</b> from {prop.issue_date}</div>
              <div>Version: <b>v{prop.version_number}</b></div>
            </div>
          </div>

          {(prop.status === "approved" || prop.status === "issued" || prop.client_signature) && (
            <div className="bg-white border border-slate-200 p-5" data-testid="client-sign-panel">
              <div className="flex items-center gap-2">
                <Signature size={16} weight="fill" className="text-emerald-600"/>
                <div className="label-micro">Client Signature</div>
              </div>
              {prop.client_signature ? (
                <div className="mt-3">
                  <div className="border border-slate-200 bg-slate-50 rounded-sm p-2">
                    <img src={prop.client_signature.data_url} alt="Client signature" className="max-h-16 object-contain" data-testid="client-sig-img"/>
                  </div>
                  <div className="text-sm font-semibold text-slate-900 mt-2">{prop.client_signature.name}</div>
                  {prop.client_signature.designation && <div className="text-xs text-slate-600">{prop.client_signature.designation}</div>}
                  <div className="text-xs text-slate-500 mt-1">Signed {new Date(prop.client_signature.signed_at).toLocaleString()}</div>
                  {prop.client_signature.note && <div className="mt-2 text-xs text-slate-700 italic border-l-2 border-emerald-500 bg-emerald-50 pl-2 py-1">"{prop.client_signature.note}"</div>}
                </div>
              ) : (
                <div className="mt-2">
                  <div className="text-xs text-slate-500">The client hasn't signed yet. Share the link below.</div>
                  {prop.client_sign_token ? (
                    <>
                      <div className="mt-3 text-[11px] font-mono bg-slate-50 border border-slate-200 rounded-sm px-2 py-1.5 break-all" data-testid="client-sign-url">
                        {window.location.origin}/sign/{prop.client_sign_token}
                      </div>
                      <div className="flex gap-2 mt-2 flex-wrap">
                        <button onClick={generateClientSignLink} data-testid="btn-copy-sign-link" className="text-[11px] inline-flex items-center gap-1 border border-slate-300 rounded-sm px-2 py-1 hover:bg-slate-900 hover:text-white transition-colors">
                          <Copy size={11}/> Copy link
                        </button>
                        <button onClick={revokeClientSignLink} data-testid="btn-revoke-sign-link" className="text-[11px] inline-flex items-center gap-1 border border-red-300 text-red-600 rounded-sm px-2 py-1 hover:bg-red-50">
                          <Trash size={11}/> Revoke
                        </button>
                      </div>
                    </>
                  ) : (
                    <button onClick={generateClientSignLink} disabled={clientSignBusy} data-testid="btn-generate-sign-link"
                            className="mt-2 text-xs inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm px-3 py-1.5 disabled:opacity-60 transition-colors">
                      <Signature size={12}/> {clientSignBusy ? "…" : "Generate signing link"}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="bg-white border border-slate-200 p-5" data-testid="approvers-panel">
            <div className="flex items-center justify-between">
              <div className="label-micro">Approvers</div>
              <span className="text-xs text-slate-500">{(prop.approver_user_ids || []).length} assigned</span>
            </div>
            {approvers.length === 0 ? (
              <div className="text-xs text-slate-500 mt-2">No approver-role users. Ask an admin to grant the approver role.</div>
            ) : (
              <div className="mt-2 space-y-1.5">
                {approvers.map(a => {
                  const on = (prop.approver_user_ids || []).includes(a.id);
                  return (
                    <label key={a.id} className="flex items-center gap-2 text-xs cursor-pointer" data-testid={`approver-toggle-${a.id}`}>
                      <input type="checkbox" checked={on} disabled={isReadOnly} onChange={() => {
                        const cur = prop.approver_user_ids || [];
                        const next = cur.includes(a.id) ? cur.filter(x => x !== a.id) : [...cur, a.id];
                        savePatch({ approver_user_ids: next });
                      }}/>
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-900 truncate">{a.full_name}</div>
                        <div className="text-slate-500 truncate">{a.email}</div>
                      </div>
                    </label>
                  );
                })}
                <div className="text-[10px] text-slate-400 pt-1">Empty = notify all approvers on submit.</div>
              </div>
            )}
          </div>

          {(prop.hubspot_contact_id || prop.hubspot_company_id || prop.hubspot_deal_id) && (
            <div className="bg-white border border-slate-200 p-5" data-testid="hubspot-panel">
              <div className="flex items-center gap-2">
                <Plug size={16} weight="fill" className="text-orange-600"/>
                <div className="label-micro">HubSpot</div>
              </div>
              <div className="text-sm mt-2 space-y-1">
                {prop.hubspot_deal_id && <div>Deal ID: <b className="font-mono text-xs">{prop.hubspot_deal_id}</b></div>}
                {prop.hubspot_contact_id && <div>Contact ID: <b className="font-mono text-xs">{prop.hubspot_contact_id}</b></div>}
                {prop.hubspot_company_id && <div>Company ID: <b className="font-mono text-xs">{prop.hubspot_company_id}</b></div>}
                {prop.hubspot_sync_status && (
                  <div>Status: <b className={prop.hubspot_sync_status === "synced" ? "text-emerald-700" : "text-red-600"}>{prop.hubspot_sync_status}</b></div>
                )}
                {prop.hubspot_last_synced_at && <div className="text-xs text-slate-500">Last push · {new Date(prop.hubspot_last_synced_at).toLocaleString()}</div>}
              </div>
            </div>
          )}
        </aside>
      </div>

      {submitPrev && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" data-testid="submit-preview-modal" onClick={() => !submitPrev.busy && setSubmitPrev(null)}>
          <div className="bg-white w-full max-w-2xl border border-slate-200 rounded-sm shadow-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="label-micro">{submitPrev.mode === "submit" ? "Submit for approval" : "Approve proposal"}</div>
                <div className="font-display font-bold text-lg text-slate-900 mt-0.5">
                  {submitPrev.mode === "submit" ? "Preview & submit" : "Approve & notify submitter"}
                </div>
              </div>
              <button onClick={() => setSubmitPrev(null)} disabled={submitPrev.busy} data-testid="submit-modal-close" className="p-1 hover:bg-slate-100 rounded-sm disabled:opacity-50"><X size={16}/></button>
            </div>
            <div className="px-5 py-4 space-y-4 overflow-y-auto">
              {submitPrev.mode === "submit" && (
                <>
                  <div>
                    <div className="label-micro mb-1">Recipients ({(submitPrev.recipients || []).length})</div>
                    {submitPrev.busy && !submitPrev.subject ? (
                      <div className="text-xs text-slate-500">Loading preview…</div>
                    ) : (submitPrev.recipients || []).length === 0 ? (
                      <div className="text-xs text-slate-500 border-l-2 border-amber-500 bg-amber-50 pl-3 py-2">No approvers found. Assign one in the sidebar first or the submit will notify nobody.</div>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {submitPrev.recipients.map((r) => (
                          <span key={r.id} className="inline-flex items-center gap-1 text-[11px] bg-blue-100 text-blue-800 px-2 py-0.5 rounded-sm">
                            <b>{r.full_name}</b><span className="text-blue-600">·</span>{r.email}
                          </span>
                        ))}
                      </div>
                    )}
                    {submitPrev.fallback && <div className="text-[11px] text-slate-500 mt-1">Fallback: notifying all approver-role users because none were assigned.</div>}
                  </div>
                  <div>
                    <div className="label-micro">Subject</div>
                    <div className="text-sm font-semibold text-slate-900 mt-1 truncate">{submitPrev.subject || "—"}</div>
                  </div>
                </>
              )}
              <div>
                <label className="block">
                  <span className="label-micro">
                    {submitPrev.mode === "submit" ? "Personal note (optional)" : "Note for the submitter (optional)"}
                  </span>
                  <textarea rows={4} value={submitPrev.note} onChange={(e) => refreshPreviewNote(e.target.value)}
                            placeholder={submitPrev.mode === "submit" ? "Any context that will help the approver? e.g. Please prioritise — client asked for the final quote by Friday." : "e.g. Looks great — cleared for issue."}
                            data-testid="submit-modal-note"
                            className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 leading-relaxed"/>
                </label>
                <div className="text-[11px] text-slate-500 mt-1">
                  {submitPrev.mode === "submit"
                    ? "This note appears in the approval email and is stored in the audit log."
                    : "This note is emailed to the submitter along with the approval."}
                </div>
              </div>
              {submitPrev.mode === "submit" && submitPrev.subject && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-slate-600 hover:text-slate-900">Preview full email body</summary>
                  <div className="mt-2 border border-slate-200 rounded-sm max-h-64 overflow-y-auto">
                    <iframe title="email-preview" srcDoc={buildPreviewHtml(submitPrev)} className="w-full h-64 border-0" data-testid="submit-modal-email-preview"/>
                  </div>
                </details>
              )}
            </div>
            <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
              <button onClick={() => setSubmitPrev(null)} disabled={submitPrev.busy} data-testid="submit-modal-cancel" className="text-sm border border-slate-300 rounded-sm px-4 py-2 hover:bg-slate-100 disabled:opacity-50">Cancel</button>
              <button onClick={submitPrev.mode === "submit" ? confirmSubmit : confirmApprove} disabled={submitPrev.busy} data-testid="submit-modal-confirm"
                      className={`text-sm rounded-sm px-4 py-2 text-white inline-flex items-center gap-2 transition-colors ${submitPrev.mode === "submit" ? "bg-slate-900 hover:bg-blue-700" : "bg-emerald-600 hover:bg-emerald-700"} disabled:opacity-60`}>
                <PaperPlaneTilt size={14}/>
                {submitPrev.busy ? "Working…" : submitPrev.mode === "submit" ? "Submit & notify" : "Approve & notify"}
              </button>
            </div>
          </div>
        </div>
      )}

      {rejectPrev && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" data-testid="reject-modal" onClick={() => !rejectPrev.busy && setRejectPrev(null)}>
          <div className="bg-white w-full max-w-lg border border-slate-200 rounded-sm shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <div className="font-display font-bold text-lg text-slate-900">Send back to draft</div>
              <button onClick={() => setRejectPrev(null)} disabled={rejectPrev.busy} data-testid="reject-modal-close" className="p-1 hover:bg-slate-100 rounded-sm disabled:opacity-50"><X size={16}/></button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <p className="text-sm text-slate-700">The submitter will receive an in-app notification and an email with your reason.</p>
              <label className="block">
                <span className="label-micro">Reason (recommended)</span>
                <textarea rows={4} value={rejectPrev.reason} onChange={(e) => setRejectPrev({ ...rejectPrev, reason: e.target.value })}
                          placeholder="e.g. Please add discount justification for the CCTV_MONTHLY line before resubmitting."
                          data-testid="reject-modal-reason"
                          className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 leading-relaxed"/>
              </label>
            </div>
            <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
              <button onClick={() => setRejectPrev(null)} disabled={rejectPrev.busy} data-testid="reject-modal-cancel" className="text-sm border border-slate-300 rounded-sm px-4 py-2 hover:bg-slate-100 disabled:opacity-50">Cancel</button>
              <button onClick={confirmReject} disabled={rejectPrev.busy} data-testid="reject-modal-confirm"
                      className="text-sm rounded-sm px-4 py-2 text-white inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-60">
                {rejectPrev.busy ? "Working…" : "Send back & notify"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Best-effort inline preview HTML — swaps in the current note without an extra API call.
function buildPreviewHtml(p) {
  const escape = (s) => (s || "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
  const noteBlock = p.note && p.note.trim()
    ? `<div style="margin:16px 0;border-left:3px solid #0f172a;background:#f1f5f9;padding:10px 14px"><div style="font-size:11px;letter-spacing:1.2px;color:#475569;text-transform:uppercase;font-weight:700;margin-bottom:6px">Note</div><div style="font-size:14px;color:#0f172a;line-height:1.5;white-space:pre-wrap">${escape(p.note)}</div></div>`
    : "";
  return `<html><body style="font-family:Arial,sans-serif;padding:16px;color:#0f172a">
    <div style="font-size:11px;letter-spacing:1.5px;color:#64748b;text-transform:uppercase;font-weight:700">TNAI Sales Suite · Approval Requested</div>
    <h2 style="font-size:18px;margin:8px 0 12px 0">${escape(p.subject || "")}</h2>
    ${noteBlock}
    <p style="font-size:13px;color:#334155;line-height:1.5">Approvers will get an "Open proposal in Sales Suite" button that jumps to the detail page.</p>
  </body></html>`;
}
